import React, { useEffect, useState } from 'react';
import { FaPhone, FaWhatsapp, FaTimes, FaStar } from 'react-icons/fa';
import ClientAvatar from './ClientAvatar';
import ClientAiProfile from './ClientAiProfile';
import { getActivities, getDeals, getReminders } from '../utils/api';

function Field({ label, value, isRTL }) {
  if (value == null || value === '') return null;
  return (
    <div
      style={{
        padding: 10,
        borderRadius: 8,
        border: '1px solid #eee8df',
        background: '#faf8f3',
        textAlign: isRTL ? 'right' : 'left',
      }}
    >
      <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a', wordBreak: 'break-word' }}>{value}</div>
    </div>
  );
}

function SectionTitle({ children, isRTL }) {
  return (
    <div
      style={{
        fontSize: 13,
        fontWeight: 700,
        color: '#0f172a',
        margin: '16px 0 8px',
        textAlign: isRTL ? 'right' : 'left',
      }}
    >
      {children}
    </div>
  );
}

const SOURCE_LABELS = {
  website: { ar: 'موقع إلكتروني', en: 'Website' },
  social_media: { ar: 'وسائل التواصل', en: 'Social media' },
  referral: { ar: 'إحالة', en: 'Referral' },
  advertisement: { ar: 'إعلان', en: 'Advertisement' },
  visit: { ar: 'زيارة', en: 'Visit' },
  whatsapp: { ar: 'واتساب', en: 'WhatsApp' },
  bayut: { ar: 'بيوت', en: 'Bayut' },
  aqar: { ar: 'عقار', en: 'Aqar' },
  other: { ar: 'أخرى', en: 'Other' },
};

const INTEREST_LABELS = {
  buy: { ar: 'شراء', en: 'Buy' },
  rent: { ar: 'إيجار', en: 'Rent' },
  sell: { ar: 'بيع', en: 'Sell' },
  invest: { ar: 'استثمار', en: 'Invest' },
  investment: { ar: 'استثمار', en: 'Investment' },
  inquiry: { ar: 'استفسار', en: 'Inquiry' },
};

const PROPERTY_LABELS = {
  apartment: { ar: 'شقة', en: 'Apartment' },
  villa: { ar: 'فيلا', en: 'Villa' },
  land: { ar: 'أرض', en: 'Land' },
  office: { ar: 'مكتب', en: 'Office' },
  commercial: { ar: 'تجاري', en: 'Commercial' },
  warehouse: { ar: 'مستودع', en: 'Warehouse' },
  other: { ar: 'أخرى', en: 'Other' },
};

const ACTIVITY_LABELS = {
  call: { ar: 'اتصال', en: 'Call' },
  whatsapp: { ar: 'واتساب', en: 'WhatsApp' },
  email: { ar: 'بريد', en: 'Email' },
  meeting: { ar: 'اجتماع', en: 'Meeting' },
  note: { ar: 'ملاحظة', en: 'Note' },
  visit: { ar: 'زيارة', en: 'Visit' },
};

function digitsOnly(value) {
  return String(value || '').replace(/\D/g, '');
}

function localizeToken(token, map, language) {
  const key = String(token || '').trim().toLowerCase();
  if (!key) return null;
  if (key.startsWith('other:')) {
    const custom = key.slice(6).trim();
    return custom || (language === 'ar' ? 'أخرى' : 'Other');
  }
  if (map[key]) return map[key][language] || map[key].en;
  return token;
}

function formatListField(raw, map, language) {
  if (raw == null || raw === '') return null;
  let parts = [];
  if (Array.isArray(raw)) {
    parts = raw;
  } else {
    const s = String(raw).trim();
    if (!s) return null;
    try {
      const parsed = JSON.parse(s);
      if (Array.isArray(parsed)) parts = parsed;
      else parts = s.split(/[,|]/);
    } catch (_) {
      parts = s.split(/[,|]/);
    }
  }
  const labeled = parts
    .map((p) => localizeToken(p, map || {}, language))
    .filter(Boolean);
  return labeled.length ? labeled.join(' · ') : null;
}

function formatWhen(value, language) {
  if (!value) return null;
  try {
    return new Date(value).toLocaleString(language === 'ar' ? 'ar-SA' : 'en', {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch (_) {
    return String(value);
  }
}

export default function ClientProfilePanel({
  client,
  language = 'ar',
  userRole = 'owner',
  onEdit,
  onCall,
  onWhatsApp,
  onClose,
  onOpenDeals,
  onOpenActivities,
  onOpenReminders,
  onToggleFavorite,
}) {
  const isRTL = language === 'ar';
  const [deals, setDeals] = useState([]);
  const [activities, setActivities] = useState([]);
  const [reminders, setReminders] = useState([]);

  useEffect(() => {
    if (!client?.id) return undefined;
    let cancelled = false;
    setDeals([]);
    setActivities([]);
    setReminders([]);
    (async () => {
      try {
        const [dRes, aRes, rRes] = await Promise.all([
          getDeals('client', client.id).catch(() => ({ data: [] })),
          getActivities('client', client.id).catch(() => ({ data: [] })),
          getReminders('client', client.id).catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setDeals(Array.isArray(dRes?.data) ? dRes.data : []);
        setActivities(Array.isArray(aRes?.data) ? aRes.data : []);
        setReminders(Array.isArray(rRes?.data) ? rRes.data : []);
      } catch (_) {
        /* ignore preview errors */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client?.id]);

  if (!client) {
    return (
      <div
        style={{
          height: '100%',
          minHeight: 420,
          border: '1px solid #e8e4dc',
          borderRadius: 14,
          background: '#faf8f3',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 24,
          color: '#64748b',
          textAlign: 'center',
        }}
      >
        {language === 'ar' ? 'اختر صفاً من الجدول لعرض الملف' : 'Select a table row to view the profile'}
      </div>
    );
  }

  const typeLabel =
    client.customer_type === 'prospect'
      ? language === 'ar'
        ? 'عميل محتمل'
        : 'Prospect'
      : language === 'ar'
        ? 'عميل حالي'
        : 'Customer';
  const activeDeals = deals.filter((d) => String(d.status || '').toLowerCase() === 'active');
  const openReminders = reminders.filter((r) => !r.completed && String(r.status || '').toLowerCase() !== 'completed');
  const recentActivities = activities.slice(0, 5);

  const phoneDigits = digitsOnly(client.phone);
  const waDigits = digitsOnly(client.whatsapp);
  const nameStr = String(client.name || '').trim();
  const nameDigits = digitsOnly(nameStr);
  const nameLooksLikePhone =
    !!nameDigits &&
    nameDigits.length >= 7 &&
    nameDigits === nameStr.replace(/\D/g, '') &&
    (nameDigits === phoneDigits || nameDigits === waDigits || !phoneDigits);
  const nameIsPhone = !nameStr || nameLooksLikePhone;
  const displayTitle = nameIsPhone
    ? (client.phone || client.whatsapp || `#${client.id}`)
    : nameStr;
  const showPhoneLine = !!client.phone && !nameIsPhone;
  const showWhatsAppLine =
    !!client.whatsapp &&
    !!waDigits &&
    waDigits !== phoneDigits &&
    !(nameLooksLikePhone && nameDigits === waDigits);

  const interest = formatListField(client.interest_type, INTEREST_LABELS, language);
  const propertyPref = formatListField(client.property_preference, PROPERTY_LABELS, language);
  const sourceRaw = client.source === 'other' ? (client.source_other || client.source) : client.source;
  const source = formatListField(sourceRaw, SOURCE_LABELS, language);

  return (
    <div
      style={{
        height: '100%',
        minHeight: 420,
        border: '1px solid #e8e4dc',
        borderTop: '3px solid #ea580c',
        borderRadius: 14,
        background: '#ffffff',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        direction: isRTL ? 'rtl' : 'ltr',
      }}
    >
      <div style={{ padding: 16, overflowY: 'auto', flex: 1 }}>
        {/* Header — phone shown once */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', minWidth: 0 }}>
            <ClientAvatar name={displayTitle} phone={client.phone} seed={client.id} size={56} radius={14} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#000', lineHeight: 1.3 }}>
                {displayTitle}
              </div>
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 3 }}>
                {showPhoneLine ? `${client.phone} · ${typeLabel}` : typeLabel}
              </div>
              {(showWhatsAppLine || client.email) && (
                <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>
                  {[
                    showWhatsAppLine
                      ? `${language === 'ar' ? 'واتساب' : 'WhatsApp'} ${client.whatsapp}`
                      : null,
                    client.email,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
            {typeof onToggleFavorite === 'function' && (
              <button
                type="button"
                onClick={(e) => onToggleFavorite(client, e)}
                style={{
                  border: '1px solid #e2e8f0',
                  background: client.is_favorite ? '#fffbeb' : '#fff',
                  borderRadius: 8,
                  width: 34,
                  height: 34,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                title={language === 'ar' ? 'مفضلة' : 'Favorite'}
              >
                <FaStar style={{ color: client.is_favorite ? '#f97316' : '#d4d4d4' }} />
              </button>
            )}
            {typeof onClose === 'function' && (
              <button
                type="button"
                onClick={onClose}
                aria-label="Close"
                style={{
                  border: '1px solid #e2e8f0',
                  background: '#fff',
                  borderRadius: 8,
                  width: 34,
                  height: 34,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <FaTimes />
              </button>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
          {client.phone && (
            <button
              type="button"
              onClick={() => onCall?.(client)}
              style={{
                padding: '7px 12px',
                borderRadius: 8,
                border: '1px solid #e2e8f0',
                background: '#fff',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <FaPhone style={{ color: '#ea580c' }} /> {language === 'ar' ? 'اتصال' : 'Call'}
            </button>
          )}
          {(client.whatsapp || client.phone) && (
            <button
              type="button"
              onClick={() => onWhatsApp?.(client)}
              style={{
                padding: '7px 12px',
                borderRadius: 8,
                border: 'none',
                background: '#25D366',
                color: '#fff',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <FaWhatsapp /> {language === 'ar' ? 'واتساب' : 'WhatsApp'}
            </button>
          )}
          <button
            type="button"
            onClick={() => onEdit?.(client)}
            style={{
              padding: '7px 12px',
              borderRadius: 8,
              border: '1px solid #fed7aa',
              background: '#fff7ed',
              color: '#9a3412',
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            {language === 'ar' ? 'تعديل' : 'Edit'}
          </button>
        </div>

        {/* Agent summary — after basic contact details */}
        <div style={{ marginTop: 14 }}>
          <ClientAiProfile
            clientId={client.id}
            language={language}
            isOwner={userRole === 'owner' || userRole === 'manager'}
            listSummary={client.ai_summary || null}
            listDraft={client.ai_profile || null}
            compact
          />
        </div>

        {/* Contact & source */}
        <SectionTitle isRTL={isRTL}>{language === 'ar' ? 'التواصل والمصدر' : 'Contact & source'}</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
          <Field label={language === 'ar' ? 'العنوان' : 'Address'} value={client.address || client.address_details} isRTL={isRTL} />
          <Field label={language === 'ar' ? 'المصدر' : 'Source'} value={source} isRTL={isRTL} />
          <Field
            label={language === 'ar' ? 'أُضيف' : 'Created'}
            value={formatWhen(client.created_at, language)}
            isRTL={isRTL}
          />
        </div>

        {/* Requirements */}
        <SectionTitle isRTL={isRTL}>
          {language === 'ar' ? 'متطلبات العميل (المحفوظة)' : 'Requirements (saved)'}
        </SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8 }}>
          <Field label={language === 'ar' ? 'الغرض' : 'Interest'} value={interest} isRTL={isRTL} />
          <Field label={language === 'ar' ? 'النوع' : 'Property type'} value={propertyPref} isRTL={isRTL} />
          <Field label={language === 'ar' ? 'الميزانية' : 'Budget'} value={client.budget} isRTL={isRTL} />
        </div>
        {client.notes ? (
          <div
            style={{
              marginTop: 8,
              padding: 10,
              borderRadius: 8,
              border: '1px solid #eee8df',
              background: '#faf8f3',
            }}
          >
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 3 }}>
              {language === 'ar' ? 'ملاحظات' : 'Notes'}
            </div>
            <div style={{ fontSize: 13, color: '#334155', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
              {client.notes}
            </div>
          </div>
        ) : null}

        {/* Pipeline */}
        <SectionTitle isRTL={isRTL}>{language === 'ar' ? 'المسار' : 'Pipeline'}</SectionTitle>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <button
            type="button"
            onClick={() => onOpenDeals?.(client)}
            style={{
              textAlign: isRTL ? 'right' : 'left',
              padding: 12,
              borderRadius: 10,
              border: '1px solid #eee8df',
              background: '#faf8f3',
              cursor: 'pointer',
            }}
          >
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>
              {language === 'ar' ? 'الصفقات النشطة' : 'Active deals'}
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
              {activeDeals.length > 0
                ? language === 'ar'
                  ? `${activeDeals.length} نشطة`
                  : `${activeDeals.length} active`
                : language === 'ar'
                  ? 'لا صفقات نشطة'
                  : 'No active deals'}
            </div>
            {activeDeals[0] && (
              <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                {activeDeals[0].property_address || activeDeals[0].property_type || activeDeals[0].deal_type || '—'}
              </div>
            )}
          </button>
          <button
            type="button"
            onClick={() => onOpenReminders?.(client)}
            style={{
              textAlign: isRTL ? 'right' : 'left',
              padding: 12,
              borderRadius: 10,
              border: '1px solid #eee8df',
              background: '#faf8f3',
              cursor: 'pointer',
            }}
          >
            <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 4 }}>
              {language === 'ar' ? 'التذكيرات' : 'Reminders'}
            </div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }}>
              {openReminders.length > 0
                ? language === 'ar'
                  ? `${openReminders.length} مفتوح`
                  : `${openReminders.length} open`
                : language === 'ar'
                  ? 'لا تذكيرات مفتوحة'
                  : 'No open reminders'}
            </div>
          </button>
        </div>

        {/* Recent activity */}
        <SectionTitle isRTL={isRTL}>{language === 'ar' ? 'آخر النشاط' : 'Recent activity'}</SectionTitle>
        {recentActivities.length === 0 ? (
          <div style={{ fontSize: 13, color: '#94a3b8' }}>
            {language === 'ar' ? 'لا نشاط بعد' : 'No activity yet'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {recentActivities.map((a) => {
              const typeKey = String(a.activity_type || '').toLowerCase();
              const typeLabel =
                ACTIVITY_LABELS[typeKey]?.[language] ||
                a.title ||
                a.activity_type ||
                (language === 'ar' ? 'نشاط' : 'Activity');
              return (
                <div
                  key={a.id || `${a.activity_type}-${a.created_at}`}
                  style={{
                    padding: '8px 10px',
                    borderRadius: 8,
                    border: '1px solid #e8e4dc',
                    background: '#faf8f3',
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{typeLabel}</div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                    {formatWhen(a.created_at || a.activity_date, language)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
        <button
          type="button"
          onClick={() => onOpenActivities?.(client)}
          style={{
            marginTop: 8,
            padding: '6px 0',
            border: 'none',
            background: 'transparent',
            color: '#ea580c',
            fontWeight: 600,
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          {language === 'ar' ? 'عرض كل النشاط' : 'View all activity'}
        </button>
      </div>
    </div>
  );
}
